// Asking "are you sure" without handing the question to the browser.
//
// window.confirm was doing this everywhere, and it is the wrong instrument for
// a site that has been designed: the dialog is unstyled, it names the origin
// rather than the thing about to be destroyed, it cannot say what will actually
// happen, and on a phone it is a system sheet thrown over the very work it is
// asking about. It also blocks the main thread, which is its own small rudeness.
//
// The shape is deliberately the same as the thing it replaces, so a call site
// reads the way it always did:
//
//   if (!(await ask({ title: 'Delete this?' }))) return;
//
// One dialog is mounted for the whole app and driven from here, rather than
// every list carrying its own copy of the same markup.

import { ref } from 'vue';

const open = ref(false);
const options = ref({});

// The promise handed back to the caller, settled when a button is pressed.
let settle = null;

export function useConfirmState() {
  return { open, options };
}

export function ask({
  title,
  note = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  // Draws the confirming button as destructive. Off by default, because most
  // questions are not.
  danger = false,
} = {}) {
  // Nothing to ask on the server, and nothing there to answer it. Refusing is
  // the safe direction: it leaves whatever was about to happen undone.
  if (typeof window === 'undefined') return Promise.resolve(false);

  // A second question while one is open answers the first with "no". It should
  // not be possible from the interface, and if it happens the earlier caller
  // must not be left waiting on a promise nobody will settle.
  if (settle) settle(false);

  options.value = { title, note, confirmLabel, cancelLabel, danger };
  open.value = true;

  return new Promise((resolve) => {
    settle = resolve;
  });
}

export function answer(value) {
  open.value = false;

  const resolve = settle;
  settle = null;
  resolve?.(value === true);
}
